// Helpers para integração com Asaas via Edge Functions
import { supabase } from './supabase';

export const asaasHelpers = {
  // Criar cliente no Asaas
  async createCustomer(customerData) {
    try {
      console.log('🏗️ Criando cliente no Asaas:', customerData);
      
      // Estrutura esperada pela Edge Function
      const requestBody = {
        company_id: customerData.company_id,
        customer: {
          name: customerData.name,
          email: customerData.email,
          cpfCnpj: customerData.cpfCnpj,
          phone: customerData.phone
        }
      };

      console.log('📤 Dados enviados para Edge Function:', requestBody);

      const { data, error } = await supabase.functions.invoke('asaas-create-customer', {
        body: requestBody
      });

      if (error) {
        console.error('❌ Erro da Edge Function:', error);
        throw error;
      }
      
      console.log('✅ Cliente criado no Asaas:', data);
      return data;
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      throw error;
    }
  },

  // Verificar e garantir cliente Asaas para responsável financeiro
  async ensureCustomerExists(responsiblePersonData, companyId) {
    try {
      console.log('🔍 Verificando cliente Asaas para responsável:', responsiblePersonData.name);
      
      let existingCustomerId = null;

      // Primeiro, buscar dados atuais da pessoa no banco para verificar asaas_customer_id
      if (responsiblePersonData.id) {
        const { data: currentPersonData, error: fetchError } = await supabase
          .from('people')
          .select('asaas_customer_id')
          .eq('id', responsiblePersonData.id)
          .single();

        if (!fetchError && currentPersonData?.asaas_customer_id) {
          existingCustomerId = currentPersonData.asaas_customer_id;
          console.log('📋 Cliente encontrado no banco:', existingCustomerId);
        }
      }

      // Se passou um asaas_customer_id diretamente, usar ele
      if (!existingCustomerId && responsiblePersonData.asaas_customer_id) {
        existingCustomerId = responsiblePersonData.asaas_customer_id;
        console.log('📋 Cliente passado diretamente:', existingCustomerId);
      }

      // Se encontrou um customer_id, garantir que está salvo na tabela people
      if (existingCustomerId) {
        console.log('✅ Cliente encontrado, garantindo que está salvo na tabela people:', existingCustomerId);
        
        // Verificar se o asaas_customer_id está realmente salvo na tabela people
        if (responsiblePersonData.id) {
          console.log('💾 Atualizando asaas_customer_id na tabela people...');
          
          const { error: updateError } = await supabase
            .from('people')
            .update({ 
              asaas_customer_id: existingCustomerId,
              asaas_sync_status: 'synced',
              asaas_last_sync: new Date().toISOString()
            })
            .eq('id', responsiblePersonData.id);

          if (updateError) {
            console.warn('⚠️ Erro ao atualizar asaas_customer_id:', updateError);
            // Não falhar a operação, apenas avisar
          } else {
            console.log('✅ asaas_customer_id atualizado na tabela people');
          }
        }
        
        return {
          customer_id: existingCustomerId,
          created_new: false,
          needs_validation: true // Flag para indicar que pode precisar recriar
        };
      }

      // Criar novo cliente no Asaas
      console.log('🆕 Criando novo cliente no Asaas...');
      
      const customerData = {
        name: responsiblePersonData.name,
        email: responsiblePersonData.email,
        cpfCnpj: responsiblePersonData.cpf?.replace(/\D/g, '') || responsiblePersonData.document?.replace(/\D/g, ''),
        phone: responsiblePersonData.phone?.replace(/\D/g, ''),
        company_id: companyId
      };

      // Validar dados obrigatórios
      if (!customerData.name || !customerData.email || !customerData.cpfCnpj) {
        throw new Error('Dados incompletos do responsável: nome, email e CPF são obrigatórios');
      }

      const result = await this.createCustomer(customerData);
      
      if (!result?.customer_id) {
        throw new Error('Falha ao criar cliente: ID não retornado');
      }

      // Atualizar people table com asaas_customer_id e status
      console.log('💾 Salvando asaas_customer_id na tabela people...');
      
      const { error: updateError } = await supabase
        .from('people')
        .update({ 
          asaas_customer_id: result.customer_id,
          asaas_sync_status: 'synced',
          asaas_sync_error: null,
          asaas_last_sync: new Date().toISOString()
        })
        .eq('id', responsiblePersonData.id);

      if (updateError) {
        console.warn('⚠️ Erro ao salvar asaas_customer_id:', updateError);
        // Não falhar a operação, apenas avisar
      } else {
        console.log('✅ asaas_customer_id e status salvos na tabela people');
      }

      return {
        customer_id: result.customer_id,
        created_new: true
      };

    } catch (error) {
      console.error('❌ Erro ao garantir cliente Asaas:', error);
      throw error;
    }
  },

  // Criar boleto com retry automático em caso de cliente inválido
  async createBoletoPaymentWithRetry(paymentData, responsiblePersonData, companyId) {
    try {
      console.log('🎫 Tentando criar boleto com retry automático...');
      
      // Primeira tentativa
      try {
        const result = await this.createBoletoPayment(paymentData);
        console.log('✅ Boleto criado na primeira tentativa');
        return result;
      } catch (firstError) {
        console.log('❌ Primeira tentativa falhou:', firstError.message);
        
        // Verificar se é erro de cliente removido/inválido
        if (firstError.message?.includes('cliente removido') || 
            firstError.message?.includes('client removed') ||
            firstError.message?.includes('invalid customer')) {
          
          console.log('🔄 Cliente inválido detectado, recriando cliente...');
          
          // Forçar criação de novo cliente
          const newCustomerData = {
            id: responsiblePersonData.id,
            name: responsiblePersonData.name,
            email: responsiblePersonData.email,
            cpf: responsiblePersonData.cpf,
            document: responsiblePersonData.cpf,
            phone: responsiblePersonData.phone,
            asaas_customer_id: null // Forçar criação novo
          };

          const customerResult = await this.ensureCustomerExists(newCustomerData, companyId);
          
          // Segunda tentativa com novo customer_id
          const newPaymentData = {
            ...paymentData,
            customer_id: customerResult.customer_id
          };
          
          console.log('🔄 Segunda tentativa com novo cliente:', customerResult.customer_id);
          const result = await this.createBoletoPayment(newPaymentData);
          console.log('✅ Boleto criado na segunda tentativa');
          return result;
        } else {
          // Se não for erro de cliente, re-throw o erro original
          throw firstError;
        }
      }
    } catch (error) {
      console.error('❌ Erro final ao criar boleto com retry:', error);
      throw error;
    }
  },

  // Criar cobrança PIX
  async createPixPayment(paymentData) {
    try {
      console.log('🏗️ asaasHelpers.createPixPayment - Iniciando chamada Edge Function');
      console.log('📤 Dados enviados para Edge Function:', paymentData);
      
      const { data, error } = await supabase.functions.invoke('asaas-create-pix-payment', {
        body: paymentData
      });

      console.log('📥 Resposta da Edge Function:', { data, error });

      if (error) {
        console.error('❌ Erro da Edge Function:', error);
        throw error;
      }
      
      console.log('✅ Sucesso na Edge Function, retornando data:', data);
      return data;
    } catch (error) {
      console.error('❌ Erro ao criar PIX (asaasHelpers):', error);
      throw error;
    }
  },
  // Verificar status do pagamento
  async checkPaymentStatus(paymentId, companyId) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-check-payment-status', {
        body: { paymentId, company_id: companyId }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      throw error;
    }
  },

  // Cancelar cobrança PIX
  async cancelPixPayment(paymentId, companyId) {
    try {
      console.log('🗑️ asaasHelpers.cancelPixPayment - Iniciando cancelamento');
      console.log('📤 Dados enviados para Edge Function:', { paymentId, companyId });
      
      const { data, error } = await supabase.functions.invoke('asaas-cancel-pix-payment', {
        body: { paymentId, company_id: companyId }
      });

      console.log('📥 Resposta da Edge Function:', { data, error });

      if (error) {
        console.error('❌ Erro da Edge Function:', error);
        throw error;
      }
      
      console.log('✅ Sucesso no cancelamento, retornando data:', data);
      return data;
    } catch (error) {
      console.error('❌ Erro ao cancelar PIX (asaasHelpers):', error);
      throw error;
    }
  },

  // Configurar webhook no Asaas
  async configureWebhook(webhookData) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-configure-webhook', {
        body: webhookData
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao configurar webhook:', error);
      throw error;
    }
  },

  // Verificar status do webhook
  async getWebhookStatus(companyId) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-get-webhook-status', {
        body: { company_id: companyId }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao verificar webhook:', error);
      throw error;
    }
  },

  // Testar conexão com API Asaas
  async testConnection(companyId) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-test-connection', {
        body: { company_id: companyId }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      throw error;
    }
  },

  // Criar cobrança via Boleto
  async createBoletoPayment(paymentData) {
    try {
      console.log('🎫 Criando boleto no Asaas:', paymentData);
      
      const { data, error } = await supabase.functions.invoke('asaas-create-boleto-payment', {
        body: paymentData
      });

      console.log('📥 Resposta da Edge Function boleto:', { data, error });

      if (error) {
        console.error('❌ Erro da Edge Function boleto:', error);
        throw error;
      }
      
      console.log('✅ Boleto criado com sucesso:', data);
      return data;
    } catch (error) {
      console.error('Erro ao criar boleto:', error);
      throw error;
    }
  },

  // Criar carnê (parcelamento)
  async createInstallmentPayment(installmentData) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-create-installment-payment', {
        body: installmentData
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao criar carnê:', error);
      throw error;
    }
  },

  // Criar assinatura recorrente
  async createSubscription(subscriptionData) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-create-subscription', {
        body: subscriptionData
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao criar assinatura:', error);
      throw error;
    }
  },

  // Cancelar assinatura
  async cancelSubscription(subscriptionId, companyId) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-cancel-subscription', {
        body: { subscription_id: subscriptionId, company_id: companyId }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao cancelar assinatura:', error);
      throw error;
    }
  },

  // Listar assinaturas
  async listSubscriptions(companyId, customerId = null) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-list-subscriptions', {
        body: { company_id: companyId, customer_id: customerId }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao listar assinaturas:', error);
      throw error;
    }
  },

  // Sincronizar cliente com Asaas
  async syncCustomer(personId, companyId, forceSync = false) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-sync-customer', {
        body: { 
          person_id: personId, 
          company_id: companyId, 
          force_sync: forceSync 
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao sincronizar cliente:', error);
      throw error;
    }
  },

  // Excluir cobrança do Asaas
  async deletePayment(paymentId, companyId) {
    try {
      const { data, error } = await supabase.functions.invoke('asaas-delete-payment', {
        body: { 
          payment_id: paymentId, 
          company_id: companyId 
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao excluir cobrança:', error);
      throw error;
    }
  }
};